import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Save, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useDataCache } from '@/hooks/useDataCache';

export default function Company() {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getCompanyData, getSystemInfo, cachedData, invalidateCache } = useDataCache();

  // Company data with state management
  const [company, setCompany] = useState<{
    legalName: string;
    rfc: string;
    website: string;
    logoUrl: string | null;
  }>({
    legalName: '',
    rfc: '',
    website: '',
    logoUrl: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load data from cache on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Update local state when cached data changes
  useEffect(() => {
    if (cachedData.company) {
      setCompany({
        legalName: cachedData.company.legalName || '',
        rfc: cachedData.company.rfc || '',
        website: cachedData.company.website || '',
        logoUrl: cachedData.company.logoUrl || null,
      });
    }
  }, [cachedData.company]);

  const loadData = async () => {
    try {
      setIsLoadingCompany(true);
      setError(null);
      
      // Load both company and system data from cache
      await Promise.all([
        getCompanyData(),
        getSystemInfo()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Error al cargar los datos');
    } finally {
      setIsLoadingCompany(false);
    }
  };

  const handleSaveCompany = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/company', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          legalName: company.legalName,
          rfc: company.rfc,
          website: company.website,
        }),
      });
      
      if (response.ok) {
        // Invalidate cache and immediately refresh with new data
        invalidateCache('company');
        await getCompanyData(); // Refresh immediately to avoid staleness
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error al guardar los datos');
      }
    } catch (error) {
      console.error('Error saving company:', error);
      setError('Error al guardar los datos de la empresa');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (SVG disabled for security)
    if (!file.type.match(/^image\/(jpeg|jpg|png|gif)$/)) {
      setError('Solo se permiten archivos de imagen (JPEG, PNG, GIF) por seguridad');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('El archivo debe ser menor a 5MB');
      return;
    }

    setIsUploadingLogo(true);
    setError(null);

    const formData = new FormData();
    formData.append('logo', file);

    try {
      const response = await fetch('/api/company/logo', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        setCompany(prev => ({ ...prev, logoUrl: result.logoUrl }));
        // Invalidate cache and immediately refresh with new logo
        invalidateCache('company');
        await getCompanyData(); // Refresh immediately to avoid staleness in AppLayout
        
        // Force update of all components using the cache by triggering a small delay
        setTimeout(async () => {
          invalidateCache('company');
          await getCompanyData();
        }, 100);
        
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error al subir el logo');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      setError('Error al subir el logo');
    } finally {
      setIsUploadingLogo(false);
      // Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (isLoadingCompany) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t('app.companySettings')}</h2>
          <p className="text-muted-foreground">
            Manage your company information and branding.
          </p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelected}
        accept="image/jpeg,image/jpg,image/png,image/gif"
        className="hidden"
      />
      <div>
        <h2 className="text-3xl font-bold tracking-tight">{t('app.companySettings')}</h2>
        <p className="text-muted-foreground">
          Gestiona la información y marca de tu empresa.
        </p>
      </div>

      {/* Error display */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <p className="text-red-800">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Company Information */}
        <Card>
          <CardHeader>
            <CardTitle>Información de la {t('app.company')}</CardTitle>
            <CardDescription>Actualiza los detalles y la información legal de tu empresa</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('app.legalName')}</label>
              <Input 
                value={company.legalName} 
                onChange={(e) => setCompany({ ...company, legalName: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('app.rfc')}</label>
              <Input 
                value={company.rfc} 
                onChange={(e) => setCompany({ ...company, rfc: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('app.website')}</label>
              <Input 
                value={company.website} 
                onChange={(e) => setCompany({ ...company, website: e.target.value })}
              />
            </div>
            <Button 
              onClick={handleSaveCompany}
              disabled={isLoading}
              className={`${showSuccess ? 'bg-green-600 hover:bg-green-700' : ''}`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </>
              ) : showSuccess ? (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  ¡Guardado!
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {t('common.save')}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Logo Upload */}
        <Card>
          <CardHeader>
            <CardTitle>Logo de la {t('app.company')}</CardTitle>
            <CardDescription>Sube el logo de tu empresa para personalizar la marca. Tamaño recomendado: 80px alto x 120px ancho (h-20 w-30)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              {company.logoUrl ? (
                <div className="space-y-4">
                  <img 
                    src={company.logoUrl} 
                    alt="Company Logo" 
                    className="mx-auto h-24 w-24 object-contain rounded-lg shadow-sm" 
                  />
                  <p className="text-sm text-green-600 font-medium">Logo actualizado</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto flex items-center justify-center">
                    <Upload className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-sm text-muted-foreground">No se ha subido ningún logo</p>
                </div>
              )}
            </div>
            <Button 
              className="w-full" 
              onClick={handleLogoUpload}
              disabled={isUploadingLogo}
            >
              {isUploadingLogo ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Subiendo logo...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  {company.logoUrl ? 'Cambiar Logo' : t('app.uploadLogo')}
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* System Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('app.systemInformation')}</CardTitle>
          <CardDescription>{t('app.systemInformationDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('app.tenantId')}</p>
              <p className="text-lg font-mono">{cachedData.systemInfo?.tenantId || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('app.created')}</p>
              <p className="text-lg">
                {cachedData.systemInfo?.createdAt ? 
                  new Date(cachedData.systemInfo.createdAt).toLocaleDateString('es-ES', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  }) : 
                  'N/A'
                }
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('app.totalUsers')}</p>
              <p className="text-lg">{cachedData.systemInfo?.totalActiveUsers || 0} {t('app.activeUsers')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}